/**
 * Utility class for performing print operations on a map.
 * @module app/models/map/MapPrintTask
 */
import $ from "jquery";
import _ from "underscore";
import Backbone from "backbone";
import s from "underscore.string";
import GeoUtils from "app/utils/GeoUtils";
import RequestUtils from "app/utils/RequestUtils";
import MapView from "app/views/map/MapView";
import urlService from "app/UrlService";

var XMIN, XMAX, YMIN, YMAX;
var initialize = _.once(function () {
  var esriConfig = window.require("esri/config");
  //esriConfig.defaults.io.corsEnabledServers.push('sampleserver6.arcgisonline.com');
  esriConfig.defaults.io.corsEnabledServers.push("map.hbkengineering.com");

  esriConfig.defaults.io.proxyUrl =
    "/arcgisserver/apis/javascript/proxy/proxy.ashx";
  XMIN = MapView.MERCATORXMIN;
  XMAX = MapView.MERCATORXMAX;
  YMIN = MapView.MERCATORYMIN;
  YMAX = MapView.MERCATORYMAX;
});

//var PRINT_BASE = "https://map.hbkengineering.com/arcgis/rest/services/Utilities/PrintingTools/GPServer";
//var PRINT_URL = 'https://sampleserver6.arcgisonline.com/arcgis/rest/services/Utilities/PrintingTools/GPServer/Export%20Web%20Map%20Task';
var PRINT_URL;
var printToken;
var printTokenExpiration;

/*$.post(urlService.apiurl('arctoken/54497f88c70ad334168adedb', true), function(data){
 if(data.token){
 var cred = new esri.Credential();
 cred.expires = data.expiration;
 cred.resources = [PRINT_BASE];
 cred.scope = 'server';
 cred.server = data.server;
 cred.ssl = true;
 cred.token = data.token;
 cred.userId = data.user;
 esri.id.credentials.push(cred);
 PRINT_URL = PRINT_URL + '?token=' + data.token;
 }
 }, 'json');*/

//create a print task which will print everything on the given map within the given geometry
//if the geometry is a point or line, an extent will be used. if the geometry is a polygon,
//that polygon will be used to indicate the printing area
/**
 * Constructor. Creates a print task which prints everything visible on the given map within the geometry provided.
 * @param {esri/Map} map     The map to use for printing.
 * @param {esri/Graphic} graphic The graphic containing the geometry to print. If the geometry is a point or line, an extent will be generated from the geometry to use for printing. Otherwise, the geometry will be used directly.
 */
function MapPrintTask(map, model) {
  var GraphicsLayer = window.require("esri/layers/GraphicsLayer");
  this.map = map;
  this._model = model;
  this._initialLayer = model.originalLayer;
  this._initialLayerVisible = this._initialLayer.visible;
  this._graphicsLayer = new GraphicsLayer();
  this._labelLayer = new GraphicsLayer();
  _.extend(this, Backbone.Events);
  initialize();
}

function requestCredentials() {
  var deferred = new $.Deferred();

  window.require(
    ["esri/kernel", "esri/IdentityManagerBase", "esri/IdentityManager"],
    function (esri, IdentityManagerBase) {
      if (PRINT_URL) {
        deferred.resolve(PRINT_URL);
      } else {
        RequestUtils.post(
          urlService.apiurl("/arctoken/printing"),
          function (data) {
            if (data.token) {
              var cred = new IdentityManagerBase.Credential();

              cred.expires = data.expiration;
              cred.resources = [data.url];
              cred.scope = "server";
              cred.server = data.server;
              cred.ssl = true;
              cred.token = data.token;
              cred.userId = data.user;
              esri.id.credentials.push(cred);
              PRINT_URL =
                data.url + "/Export%20Web%20Map%20Task?token=" + data.token;
              printToken = data.token;
              printTokenExpiration = data.expiration;

              deferred.resolve(PRINT_URL);
            } else {
              deferred.reject({
                error: "No token received",
              });
            }
          },
          "json"
        );
      }
    }
  );

  return deferred.promise();
}

function createExtentFromGeometry(geometry) {
  var geometryEngine = window.require("esri/geometry/geometryEngine");
  var expandedGeometry = geometry;

  if (geometry.type == "point") {
    expandedGeometry = geometryEngine.buffer(geometry, 300, "feet");
  }
  return GeoUtils.getExtent(expandedGeometry);
}

function createPolygonFromExtent(extent) {
  var Point = window.require("esri/geometry/Point");
  var Polygon = window.require("esri/geometry/Polygon");
  var upperLeft;
  var upperLeft2;
  var upperRight;
  var lowerLeft;
  var lowerRight;

  upperLeft = new Point(extent.xmin, extent.ymin, extent.spatialReference);
  upperLeft2 = new Point(extent.xmin, extent.ymin, extent.spatialReference);
  upperRight = new Point(extent.xmax, extent.ymin, extent.spatialReference);
  lowerLeft = new Point(extent.xmin, extent.ymax, extent.spatialReference);
  lowerRight = new Point(extent.xmax, extent.ymax, extent.spatialReference);

  var result = new Polygon(extent.spatialReference);
  //we want the order in the ring to be upperLeft thru lowerRight, so iterate on the reverse, since
  //insert will reverse the order to what we want
  result.addRing([upperLeft2, lowerLeft, lowerRight, upperRight, upperLeft]);

  return result;
}

function createHole(geometry, spatialReference) {
  var Graphic = window.require("esri/graphic");
  var workingGeometry = geometry;

  if (geometry.type != "polygon") {
    workingGeometry = createPolygonFromExtent(
      createExtentFromGeometry(geometry)
    );
  }

  var outerRing = [
    //starts clockwise
    [XMIN, YMAX],
    [XMIN, YMIN],
    [XMAX, YMIN],
    [XMAX, YMAX],
    [XMIN, YMAX],
  ];
  var innerRing = workingGeometry.rings[0].slice();

  if (workingGeometry.isClockwise(innerRing)) {
    innerRing.reverse();
  }
  //inner ring is counterclockwise, outer ring is clockwise
  //outerRing.reverse();
  //innerRing.reverse();

  var clipper = {
    geometry: {
      rings: [outerRing, innerRing],
      spatialReference: spatialReference,
    },
    symbol: {
      color: [255, 255, 255],
      outline: {
        color: [0, 0, 0, 255],
        width: 0,
        type: "esriSLS",
        style: "esriSLSSolid",
      },
      type: "esriSFS",
      style: "esriSFSSolid",
    },
  };

  clipper = new Graphic(clipper);

  return clipper;
}

function wrapDojoDeferred(dojoDeferred, deferred) {
  if (!deferred) {
    deferred = new $.Deferred();
  }

  dojoDeferred.then(
    function (arg) {
      deferred.resolve(arg);
    },
    function (arg) {
      deferred.reject(arg);
    },
    function (arg) {
      deferred.notify(arg);
    }
  );

  return deferred;
}

function createLabelSymbol(model, offset, upperRight, sr) {
  var Font = window.require("esri/symbols/Font");
  var Graphic = window.require("esri/graphic");
  var TextSymbol = window.require("esri/symbols/TextSymbol");
  var Point = window.require("esri/geometry/Point");
  var layer = model.originalLayer;
  var text =
    s.humanize(layer.getDisplayNameField().get("label")) +
    " " +
    layer.getDisplayName(model);
  var pt = new Point(upperRight, sr);
  var symbol = new TextSymbol(text)
    .setHorizontalAlignment("center")
    .setVerticalAlignment("top")
    .setOffset(offset, offset)
    .setFont(new Font().setSize("30pt"));
  var result = new Graphic(pt, symbol);

  return result;
}

function geometryFromModel(model) {
  var geo;

  if (model) {
    var layer = model.originalLayer;

    geo = layer.toGraphic(model).geometry;
  }

  return geo;
}

MapPrintTask.prototype = {
  /**
   * Execute the print task.
   * @return {Promise} A promise object which, when complete, shall resolve with no arguments or, if an error occurs, shall resolve with an error object.
   */
  execute: function () {
    var deferred = new $.Deferred();
    var self = this;
    var oldCancel = self.cancel;

    self.cancel = function () {
      deferred.reject(true);
      self.cancel = oldCancel;
    };

    this.setup().done(
      _.bind(function (printTask) {
        wrapDojoDeferred(printTask.execute(this.parameters())).then(
          function (result) {
            if (deferred.state() == "pending") {
              //check to make sure it wasn't cancelled already
              deferred.resolve({
                url: result.url + "?token=" + printToken,
              });
            }
          },
          function (err) {
            if (deferred.state() == "pending") {
              //check to make sure it wasn't cancelled already
              deferred.reject(err);
            }
          }
        );
      }, this)
    );

    deferred.always(_.bind(this.teardown, this));

    return deferred.promise();
  },

  /**
   * Cancels the print task.
   */
  cancel: function () {},

  /**
   * Perform UI manipulations necessary to print properly.
   * @return {Promise} A promise object which shall resolve with a `PrintTask` when the setup process is complete.
   */
  setup: function () {
    //	alert('SETUP');
    var PrintTask = window.require("esri/tasks/PrintTask");
    var deferred = new $.Deferred();
    var credentialPromise = requestCredentials();
    var result = $.when(deferred, credentialPromise);
    var self = this;

    credentialPromise.then(function (printUrl) {
      if (!self.clipGraphic) {
        self.trigger("start");
        self._oldExtent = self.map.extent;
        self._clipGraphicSource = geometryFromModel(self._model);
        self.clipGraphic = createHole(
          self._clipGraphicSource,
          self.map.spatialReference
        );
        self._graphicsLayer.add(self.clipGraphic);
        var newExtent = createExtentFromGeometry(self._clipGraphicSource);

        if (
          self._clipGraphicSource.type == "polygon" ||
          self._clipGraphicSource.type == "extent"
        ) {
          self._model.originalLayer.hide();
        }

        self.map.addLayer(self._graphicsLayer);
        self.map.addLayer(self._labelLayer);
        wrapDojoDeferred(self.map.setExtent(newExtent, true))
          .fail(function (err) {
            deferred.reject(err);
          })
          .then(
            _.bind(function () {
              var pt = [
                self.map.extent.xmin +
                  (self.map.extent.xmax - self.map.extent.xmin) / 2,
                self.map.extent.ymax,
              ];

              self._textSymbol = createLabelSymbol(
                self._model,
                0,
                pt,
                self.map.spatialReference
              );
              self._labelLayer.add(self._textSymbol);

              deferred.resolve(new PrintTask(printUrl));
            }, self)
          );
      } else {
        deferred.resolve(new PrintTask(printUrl));
      }
    });

    return result;
  },

  /**
   * *Undo* UI manipulations performed by `setup`.
   * @return {Promise} A promise object which shall resolve when teardown is complete.
   */
  teardown: function () {
    var deferred = new $.Deferred();
    //				alert('TEARDOWN');

    if (this.clipGraphic) {
      this.map.removeLayer(this._graphicsLayer);
      this.map.removeLayer(this._labelLayer);
      this._graphicsLayer.remove(this.clipGraphic);
      this._labelLayer.remove(this._textSymbol);

      if (
        this._clipGraphicSource.type == "polygon" ||
        this._clipGraphicSource.type == "extent"
      ) {
        this._model.originalLayer.show();
      }

      this.clipGraphic = null;
      wrapDojoDeferred(
        this.map.setExtent(this._oldExtent, true),
        deferred
      ).done(
        _.bind(function () {
          this._oldExtent = null;

          this.trigger("stop");
        }, this)
      );
    } else {
      deferred.resolve();
    }

    return deferred.promise();
  },

  /**
   * Gets the template used for printing. The result of this function can be used to modify the print template.
   * @return {esri/tasks/PrintTemplate} The print template.
   */
  template: function () {
    var PrintTemplate = window.require("esri/tasks/PrintTemplate");
    var template = this._template;

    if (!this._template) {
      template = this._template = new PrintTemplate();
    }
    //template.layout = 'A3 Landscape';
    template.layout = "MAP_ONLY";

    /* Define whether the printed map will preserve the map scale or the map extent.
		 Only one of these properties can be preserved to produce a high-resolution map
		 for printing. If preserveScale is set to true, the map displayed in the print
		 window will match the current scale of the map in the application. If preserveScale
		 is false, the current extent of the map in the application displays in the printed
		 map but the scale may change. The default value is true.*/
    template.preserveScale = true;
    template.format = "PNG32";
    template.layoutOptions = {
      showAttribution: false,
    };

    var DPI = 200;
    var A3_HEIGHT_INCHES = DPI * 16.5;
    var A3_WIDTH_INCHES = DPI * 11.7;

    template.exportOptions = {
      width: A3_HEIGHT_INCHES, //we're landscape, remember?
      height: A3_WIDTH_INCHES,
      dpi: DPI,
    };

    return template;
  },

  /**
   * Gets the parameters used for printing. The result of this function can be used to modify the print parameters.
   * @return {esri/tasks/PrintParameters} The print parameters.
   */
  parameters: function () {
    var PrintParameters = window.require("esri/tasks/PrintParameters");
    var params = this._params;

    if (!this._params) {
      params = this._params = new PrintParameters();
    }

    params.map = this.map;
    params.template = this.template();

    return params;
  },
};

export default MapPrintTask;



//////////////////
// WEBPACK FOOTER
// ./public/js/app/models/map/MapPrintTask.js
// module id = 509
// module chunks = 1